import * as THREE from 'three';
import { createSeededRandom } from '../utils/random';

/**
 * Speed-threshold foam for a moving hull: bow spray, a spreading V-wake, a
 * turbulent churn behind the stern, and occasional side spray.
 *
 * `THREE.Points` + a small `ShaderMaterial` keep the whole effect in a single
 * draw call with per-particle alpha/size/colour (which `PointsMaterial`
 * cannot express). Everything is world-space and recycled from a fixed pool,
 * so the trail is left behind as the ship advances instead of travelling with
 * the hull.
 *
 * Two design rules keep it readable:
 * - Nothing is emitted below `MIN_WAKE_SPEED`, and intensity ramps to 1 at
 *   `FULL_WAKE_SPEED`, so a drifting hull leaves no wake.
 * - Foam is sampled onto the shared Gerstner surface every frame, so it rides
 *   the same waves the ship and the server-buoyancy use.
 *
 * All randomness flows through `createSeededRandom`, matching the rest of the
 * project: screenshots and bot playtests stay reproducible.
 */

/** Below this speed (m/s) the ship is treated as drifting and emits nothing. */
const MIN_WAKE_SPEED = 0.5;
/** Speed (m/s) at which the wake reaches full intensity. */
const FULL_WAKE_SPEED = 7;
/** Hard cap on live particles; older foam is recycled when the pool is full. */
const DEFAULT_MAX_PARTICLES = 420;
/** Downward acceleration on airborne spray (m/s²). */
const GRAVITY = 5.0;
/** Horizontal drag so spray does not slide forever across the water. */
const DRAG = 2.4;
const SEED = 0x5a1e;

// Ship-local spawn geometry (bow is +X, port is -Z).
const BOW_X = 5.4;
const BOW_HALF_WIDTH = 1.3;
const HULL_HALF_WIDTH = 1.95;
const SIDE_MIN_X = -2.6;
const SIDE_MAX_X = 3.6;

// Foam reads against the bright, near-white water by staying slightly cooler
// and more opaque than the surface highlights; bow spray stays near-white.
const WAKE_COLOR = new THREE.Color(0xbfe7f6);
const SPRAY_COLOR = new THREE.Color(0xffffff);

interface WakeParticle {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  age: number;
  life: number;
  size: number;
  alpha: number;
  color: THREE.Color;
}

const VERTEX_SHADER = /* glsl */ `
  attribute float aAlpha;
  attribute float aSize;
  attribute vec3 aColor;

  uniform float uScale;

  varying float vAlpha;
  varying vec3 vColor;

  #include <fog_pars_vertex>

  void main() {
    vAlpha = aAlpha;
    vColor = aColor;

    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    float depth = -mvPosition.z;
    gl_PointSize = (aAlpha > 0.0 && depth > 0.0) ? aSize * (uScale / depth) : 0.0;
    gl_Position = projectionMatrix * mvPosition;

    #include <fog_vertex>
  }
`;

const FRAGMENT_SHADER = /* glsl */ `
  varying float vAlpha;
  varying vec3 vColor;

  #include <fog_pars_fragment>

  void main() {
    if (vAlpha <= 0.002) discard;

    // Soft round puff: opaque core, feathered rim, no texture needed.
    vec2 uv = gl_PointCoord - vec2(0.5);
    float disc = smoothstep(0.5, 0.16, length(uv));
    if (disc <= 0.004) discard;

    gl_FragColor = vec4(vColor, vAlpha * disc);

    #include <fog_fragment>
  }
`;

export class ShipWake {
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.ShaderMaterial;
  private readonly points: THREE.Points;
  private readonly particles: WakeParticle[] = [];
  private readonly maxParticles: number;
  private readonly rng = createSeededRandom(SEED);

  private readonly positions: Float32Array;
  private readonly alphas: Float32Array;
  private readonly sizes: Float32Array;
  private readonly colors: Float32Array;

  private cursor = 0;
  private bowAccumulator = 0;
  private splashAccumulator = 0;
  private sprayAccumulator = 0;
  private churnAccumulator = 0;
  private intensity = 0;
  /** Sea-state spray amount, 0..1; scales all wake and foam emission. */
  private sprayAmount = 1;

  /**
   * @param scene     Scene the wake is added to (world space, not the ship).
   * @param heightAt  Water height sampler `(x, z) => y` used to keep foam on
   *                  the shared Gerstner surface.
   */
  constructor(
    scene: THREE.Scene,
    private readonly heightAt: (x: number, z: number) => number,
    maxParticles = DEFAULT_MAX_PARTICLES,
  ) {
    this.maxParticles = maxParticles;
    this.positions = new Float32Array(maxParticles * 3);
    this.alphas = new Float32Array(maxParticles);
    this.sizes = new Float32Array(maxParticles);
    this.colors = new Float32Array(maxParticles * 3);

    for (let i = 0; i < maxParticles; i += 1) {
      this.particles.push({
        position: new THREE.Vector3(0, -1000, 0),
        velocity: new THREE.Vector3(),
        age: 0,
        life: 0,
        size: 0,
        alpha: 0,
        color: new THREE.Color(),
      });
      this.positions[i * 3 + 1] = -1000;
    }

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute(
      'position',
      dynamicAttribute(new THREE.BufferAttribute(this.positions, 3)),
    );
    this.geometry.setAttribute('aAlpha', dynamicAttribute(new THREE.BufferAttribute(this.alphas, 1)));
    this.geometry.setAttribute('aSize', dynamicAttribute(new THREE.BufferAttribute(this.sizes, 1)));
    this.geometry.setAttribute('aColor', dynamicAttribute(new THREE.BufferAttribute(this.colors, 3)));
    this.geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), Infinity);

    this.material = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.merge([
        THREE.UniformsLib.fog,
        { uScale: { value: 800 } },
      ]),
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
      fog: true,
      blending: THREE.NormalBlending,
    });

    this.points = new THREE.Points(this.geometry, this.material);
    this.points.name = 'ship-wake';
    this.points.frustumCulled = false;
    this.points.renderOrder = 2;
    scene.add(this.points);
  }

  /**
   * Convert world-metre particle sizes into pixels for the current viewport.
   * `bufferHeight` is the drawing-buffer height (thus DPR-aware) and `fov` the
   * camera vertical field of view in degrees.
   */
  setViewport(bufferHeight: number, fov: number): void {
    const scale = bufferHeight / (2 * Math.tan(THREE.MathUtils.degToRad(fov) * 0.5));
    this.material.uniforms.uScale.value = Number.isFinite(scale) && scale > 1 ? scale : 800;
  }

  /**
   * Scale the whole wake with the sea state's spray amount. Calm water (0)
   * suppresses the wake entirely; storm water (1) leaves it at full strength.
   */
  setSpray(amount: number): void {
    this.sprayAmount = THREE.MathUtils.clamp(amount, 0, 1);
  }

  /** Speed ramp shared by emission and lifetime so fast hulls leave a longer trail. */
  private wakeSpeedFactor(speed: number): number {
    return THREE.MathUtils.clamp((speed - MIN_WAKE_SPEED) / (FULL_WAKE_SPEED - MIN_WAKE_SPEED), 0, 1);
  }

  /**
   * Advance the wake for one frame.
   *
   * @param delta     Seconds since the previous update.
   * @param position  Ship origin in world space.
   * @param heading   Hull yaw (radians), bow along +X.
   * @param speed     Forward speed (m/s); drives emission rate and wake width.
   * @param active    False suppresses new foam (e.g. a defeated wreck) while
   *                  existing particles still fade out.
   */
  update(
    delta: number,
    position: THREE.Vector3,
    heading: number,
    speed: number,
    active = true,
  ): void {
    const dt = Math.min(Math.max(delta, 0), 0.05);
    this.intensity = active
      ? THREE.MathUtils.clamp((speed - MIN_WAKE_SPEED) / (FULL_WAKE_SPEED - MIN_WAKE_SPEED), 0, 1) * this.sprayAmount
      : 0;

    if (this.intensity > 0) this.emit(dt, position, heading, speed);
    this.integrate(dt);

    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.attributes.aAlpha.needsUpdate = true;
    this.geometry.attributes.aSize.needsUpdate = true;
    this.geometry.attributes.aColor.needsUpdate = true;
  }

  dispose(): void {
    this.points.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
    this.particles.length = 0;
  }

  // --- emission ----------------------------------------------------------

  private emit(dt: number, position: THREE.Vector3, heading: number, speed: number): void {
    // Rotate ship-local directions into world space. With the bow along +X the
    // forward basis is (cos, -sin) and the local +Z (port) basis is (sin, cos).
    const forwardX = Math.cos(heading);
    const forwardZ = -Math.sin(heading);
    const lateralX = Math.sin(heading);
    const lateralZ = Math.cos(heading);
    const intensity = this.intensity;

    // Emission rates scale with speed, so a slow hull barely foams.
    this.bowAccumulator += (60 * intensity) * dt;
    while (this.bowAccumulator >= 1) {
      this.bowAccumulator -= 1;
      this.spawnBowWake(position, forwardX, forwardZ, lateralX, lateralZ, speed);
    }

    this.churnAccumulator += (34 * intensity) * dt;
    while (this.churnAccumulator >= 1) {
      this.churnAccumulator -= 1;
      this.spawnSternChurn(position, forwardX, forwardZ, lateralX, lateralZ);
    }

    this.splashAccumulator += (16 * intensity) * dt;
    while (this.splashAccumulator >= 1) {
      this.splashAccumulator -= 1;
      this.spawnBowSplash(position, forwardX, forwardZ, lateralX, lateralZ, speed);
    }

    this.sprayAccumulator += (12 * intensity) * dt;
    while (this.sprayAccumulator >= 1) {
      this.sprayAccumulator -= 1;
      this.spawnHullSpray(position, forwardX, forwardZ, lateralX, lateralZ);
    }
  }

  /** Long-lived foam pair forming the V trail at the bow. */
  private spawnBowWake(
    position: THREE.Vector3,
    forwardX: number,
    forwardZ: number,
    lateralX: number,
    lateralZ: number,
    speed: number,
  ): void {
    const side = this.rng() < 0.5 ? -1 : 1;
    const localX = BOW_X + (this.rng() - 0.5) * 0.9;
    const localZ = side * (BOW_HALF_WIDTH + this.rng() * 0.4);
    const x = position.x + forwardX * localX + lateralX * localZ;
    const z = position.z + forwardZ * localX + lateralZ * localZ;

    // Lateral speed grows with hull speed -> the V opens wider the faster it goes.
    const outward = (0.32 * speed + 0.3 + this.rng() * 0.45) * side;
    const backward = 0.12 * speed + 0.15;
    const jitterX = (this.rng() - 0.5) * 0.3;
    const jitterZ = (this.rng() - 0.5) * 0.3;

    this.spawn(
      x,
      this.heightAt(x, z) + 0.2,
      z,
      forwardX * -backward + lateralX * outward + jitterX,
      0.1 + this.rng() * 0.25,
      forwardZ * -backward + lateralZ * outward + jitterZ,
      (2.2 + this.rng() * 2.0) * (1 + this.sprayAmount * 0.9) * (1 + this.wakeSpeedFactor(speed) * 0.5),
      (1.1 + this.rng() * 1.1) * (1 + this.sprayAmount * 0.35),
      0.6,
      WAKE_COLOR,
    );
  }

  /** Turbulent foam churned straight out behind the stern. */
  private spawnSternChurn(
    position: THREE.Vector3,
    forwardX: number,
    forwardZ: number,
    lateralX: number,
    lateralZ: number,
  ): void {
    const localX = -5.4 - this.rng() * 0.9;
    const localZ = (this.rng() - 0.5) * 1.7;
    const x = position.x + forwardX * localX + lateralX * localZ;
    const z = position.z + forwardZ * localX + lateralZ * localZ;

    this.spawn(
      x,
      this.heightAt(x, z) + 0.18,
      z,
      (this.rng() - 0.5) * 0.6,
      0.1 + this.rng() * 0.2,
      (this.rng() - 0.5) * 0.6,
      (1.7 + this.rng() * 1.6) * (1 + this.sprayAmount * 0.9),
      (0.9 + this.rng() * 0.9) * (1 + this.sprayAmount * 0.3),
      0.5,
      WAKE_COLOR,
    );
  }

  /** Short arc of bright spray thrown up off the bow. */
  private spawnBowSplash(
    position: THREE.Vector3,
    forwardX: number,
    forwardZ: number,
    lateralX: number,
    lateralZ: number,
    speed: number,
  ): void {
    const side = this.rng() < 0.5 ? -1 : 1;
    const localX = BOW_X + 0.3 + this.rng() * 0.4;
    const localZ = side * (0.3 + this.rng() * 0.9);
    const x = position.x + forwardX * localX + lateralX * localZ;
    const z = position.z + forwardZ * localX + lateralZ * localZ;

    const forward = 0.2 + this.rng() * 0.4;
    const outward = (0.5 + this.rng() * 0.8) * side;

    this.spawn(
      x,
      this.heightAt(x, z) + 0.4,
      z,
      forwardX * forward + lateralX * outward,
      1.6 + this.rng() * 1.8 + speed * 0.12,
      forwardZ * forward + lateralZ * outward,
      (0.6 + this.rng() * 0.7) * (1 + this.sprayAmount * 0.6),
      (0.45 + this.rng() * 0.55) * (1 + this.sprayAmount * 0.35),
      0.8,
      SPRAY_COLOR,
    );
  }

  /** Occasional small spray flicked off the sides amidships. */
  private spawnHullSpray(
    position: THREE.Vector3,
    forwardX: number,
    forwardZ: number,
    lateralX: number,
    lateralZ: number,
  ): void {
    const side = this.rng() < 0.5 ? -1 : 1;
    const localX = SIDE_MIN_X + this.rng() * (SIDE_MAX_X - SIDE_MIN_X);
    const localZ = side * (HULL_HALF_WIDTH + this.rng() * 0.15);
    const x = position.x + forwardX * localX + lateralX * localZ;
    const z = position.z + forwardZ * localX + lateralZ * localZ;

    const forward = -0.1 + this.rng() * 0.3;
    const outward = (0.6 + this.rng() * 0.9) * side;

    this.spawn(
      x,
      this.heightAt(x, z) + 0.3,
      z,
      forwardX * forward + lateralX * outward,
      0.8 + this.rng() * 1.2,
      forwardZ * forward + lateralZ * outward,
      (0.55 + this.rng() * 0.7) * (1 + this.sprayAmount * 0.6),
      (0.4 + this.rng() * 0.6) * (1 + this.sprayAmount * 0.3),
      0.6,
      SPRAY_COLOR,
    );
  }

  /** Claim the next pool slot (recycling the oldest when full). */
  private spawn(
    x: number,
    y: number,
    z: number,
    vx: number,
    vy: number,
    vz: number,
    life: number,
    size: number,
    alpha: number,
    color: THREE.Color,
  ): void {
    const particle = this.particles[this.cursor];
    this.cursor = (this.cursor + 1) % this.maxParticles;
    particle.position.set(x, y, z);
    particle.velocity.set(vx, vy, vz);
    particle.age = 0;
    particle.life = life;
    particle.size = size;
    particle.alpha = alpha;
    particle.color.copy(color);
  }

  // --- simulation --------------------------------------------------------

  private integrate(dt: number): void {
    const drag = Math.exp(-DRAG * dt);
    for (let i = 0; i < this.maxParticles; i += 1) {
      const particle = this.particles[i];
      const offset = i * 3;

      if (particle.life <= 0 || particle.age >= particle.life) {
        this.alphas[i] = 0;
        this.sizes[i] = 0;
        continue;
      }

      particle.age += dt;
      particle.velocity.y -= GRAVITY * dt;
      particle.velocity.x *= drag;
      particle.velocity.z *= drag;
      particle.position.x += particle.velocity.x * dt;
      particle.position.y += particle.velocity.y * dt;
      particle.position.z += particle.velocity.z * dt;

      // Settle onto the wave surface instead of sinking through it. The small
      // positive offset keeps the foam above the displaced surface mesh, which
      // would otherwise occlude it at the waterline.
      const water = this.heightAt(particle.position.x, particle.position.z) + 0.16;
      if (particle.position.y < water) {
        particle.position.y = water;
        if (particle.velocity.y < 0) particle.velocity.y = 0;
      }

      const t = Math.min(1, particle.age / particle.life);
      const fade = (1 - t) * (1 - t);
      const size = particle.size * (0.85 + 0.4 * t);

      this.positions[offset] = particle.position.x;
      this.positions[offset + 1] = particle.position.y;
      this.positions[offset + 2] = particle.position.z;
      this.alphas[i] = particle.alpha * fade;
      this.sizes[i] = size;
      this.colors[offset] = particle.color.r;
      this.colors[offset + 1] = particle.color.g;
      this.colors[offset + 2] = particle.color.b;
    }
  }
}

function dynamicAttribute<T extends THREE.BufferAttribute>(attribute: T): T {
  attribute.setUsage(THREE.DynamicDrawUsage);
  return attribute;
}
